/**
 * @fileoverview Modal component for assigning production plans to orders
 */

import React, { useState, useEffect } from 'react';
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Form,
  FormGroup,
  Label,
  Input,
  Alert,
  Spinner
} from 'reactstrap';
import { useAsignacionPlan } from '../hooks/useAsignacionPlan';
import { usePlanesDisponibles } from '../hooks/usePlanesDisponibles';
import { PlanCentroCostoExpanded } from '../types/collections/PlanCentroCosto';
import { PlanValidationEngine } from '../services/PlanValidationEngine';

/**
 * Props for AsignarPlanModal component
 */
export interface AsignarPlanModalProps {
  /** Whether the modal is open */
  isOpen: boolean;
  /** Function to toggle modal visibility */
  toggle: () => void;
  /** ID of the order to assign plan to */
  ordenId: string;
  /** Name/description of the order */
  ordenNombre: string;
  /** Callback function called after successful plan assignment */
  onSuccess?: (data: any) => void;
}

/**
 * Modal component for assigning production plans to orders
 */
export const AsignarPlanModal: React.FC<AsignarPlanModalProps> = ({
  isOpen,
  toggle,
  ordenId,
  ordenNombre,
  onSuccess
}) => {
  // Local state
  const [selectedPlanId, setSelectedPlanId] = useState<string>('');
  const [validationError, setValidationError] = useState<string>('');
  const [processValidation, setProcessValidation] = useState<any>(null);
  const [validationLoading, setValidationLoading] = useState<boolean>(false);

  // Custom hooks
  const {
    loading: asignacionLoading,
    error: asignacionError,
    asignarPlan,
    clearError: clearAsignacionError
  } = useAsignacionPlan();

  const {
    planes,
    loading: planesLoading,
    error: planesError,
    fetchPlanes,
    clearError,
    cancelRequests,
    getCentroCostoName
  } = usePlanesDisponibles();

  // Fetch planes when modal opens
  useEffect(() => {
    if (isOpen) {
      fetchPlanes();
      // Reset state when modal opens
      setSelectedPlanId('');
      setValidationError('');
      setProcessValidation(null);
      setValidationLoading(false);
      clearAsignacionError();
      clearError();
    } else {
      // Cancel ongoing requests when modal closes
      cancelRequests();
    }
  }, [isOpen, fetchPlanes, clearAsignacionError, clearError, cancelRequests]);

  // Cleanup function to cancel requests when component unmounts
  useEffect(() => {
    return () => {
      cancelRequests();
    };
  }, [cancelRequests]);

  /**
   * Handle plan selection change
   */
  const handlePlanChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const planId = event.target.value;
    setSelectedPlanId(planId);
    setValidationError('');
    setProcessValidation(null);
    clearAsignacionError();

    // Validate process coverage if plan is selected
    if (planId && ordenId) {
      setValidationLoading(true);
      try {
        const validationResult = await PlanValidationEngine.validatePlanForOrder(ordenId, planId);
        const validationSummary = PlanValidationEngine.getValidationSummary(validationResult);
        setProcessValidation({
          ...validationResult,
          summary: validationSummary
        });
      } catch (error) {
        console.error('Error validating plan:', error);
        setValidationError('Error al validar cobertura de procesos');
      } finally {
        setValidationLoading(false);
      }
    }
  };

  /**
   * Validate form before submission
   */
  const validateForm = (): boolean => {
    if (!selectedPlanId.trim()) {
      setValidationError('Debe seleccionar un plan');
      return false;
    }
    
    // Check process validation if available
    if (processValidation && !processValidation.isValid) {
      setValidationError('El plan seleccionado no cubre todos los procesos requeridos');
      return false;
    }
    
    setValidationError('');
    return true;
  };

  /**
   * Get current user ID from localStorage
   */
  const getCurrentUserId = (): string => {
    try {
      const authUser = localStorage.getItem('authUser');
      if (authUser) {
        const userData = JSON.parse(authUser);
        return userData.record?.id || '';
      }
    } catch (error) {
      console.error('Error getting current user:', error);
    }
    return '';
  };

  /**
   * Handle form submission
   */
  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    try {
      const currentUserId = getCurrentUserId();
      if (!currentUserId) {
        setValidationError('No se pudo obtener el usuario actual');
        return;
      }

      const result = await asignarPlan({
        ordenId,
        planId: selectedPlanId,
        usuarioId: currentUserId
      });

      if (result.success) {
        // Call success callback
        if (onSuccess) {
          onSuccess(result.data);
        }
        
        // Close modal
        toggle();
        
        // Reset form
        setSelectedPlanId('');
        setValidationError('');
      }
    } catch (error) {
      console.error('Error submitting form:', error);
    }
  };

  /**
   * Handle modal close
   */
  const handleClose = () => {
    setSelectedPlanId('');
    setValidationError('');
    setProcessValidation(null);
    setValidationLoading(false);
    clearAsignacionError();
    clearError();
    cancelRequests();
    toggle();
  };

  /**
   * Get selected plan object
   */
  const selectedPlan = planes.find(plan => plan.id === selectedPlanId);

  /**
   * Check if form can be submitted
   */
  const canSubmit = selectedPlanId.trim() !== '' && 
                   !asignacionLoading && 
                   !validationLoading &&
                   (!processValidation || processValidation.isValid);

  return (
    <Modal isOpen={isOpen} toggle={handleClose} size="lg">
      <ModalHeader toggle={handleClose}>
        <i className="fas fa-tasks me-2"></i>
        Asignar Plan a Orden
      </ModalHeader>
      
      <Form onSubmit={handleSubmit}>
        <ModalBody>
          {/* Order Information */}
          <div className="mb-4 p-3 bg-light rounded">
            <h6 className="mb-2 text-primary">
              <i className="fas fa-info-circle me-2"></i>
              Información de la Orden
            </h6>
            <div className="row">
              <div className="col-md-12">
                <strong>Orden:</strong> {ordenNombre}
              </div>
            </div>
          </div>

          {/* Plan Selection */}
          <FormGroup>
            <Label for="plan-select" className="fw-bold">
              <i className="fas fa-clipboard-list me-2"></i>
              Seleccionar Plan de Producción
            </Label>
            <Input
              id="plan-select"
              type="select"
              value={selectedPlanId}
              onChange={handlePlanChange}
              disabled={planesLoading}
              invalid={!!validationError}
            >
              <option value="">-- Seleccione un plan --</option>
              {planes.map(plan => (
                <option key={plan.id} value={plan.id}>
                  {plan.nombre}
                </option>
              ))}
            </Input>
            {validationError && (
              <div className="invalid-feedback d-block">
                {validationError}
              </div>
            )}
          </FormGroup>

          {/* Selected Plan Details */}
          {selectedPlan && (
            <div className="mb-3 p-3 border rounded">
              <h6 className="mb-2 text-success">
                <i className="fas fa-check-circle me-2"></i>
                Plan Seleccionado
              </h6>
              <div className="row">
                <div className="col-md-6">
                  <strong>Nombre:</strong> {selectedPlan.nombre}
                </div>
                <div className="col-md-6">
                  <strong>Centros de Costo:</strong> {selectedPlan.centro_costo_peso?.length || 0}
                </div>
              </div>
              

              
              {selectedPlan.centro_costo_peso && selectedPlan.centro_costo_peso.length > 0 && (
                <div className="mt-2">
                  <small className="text-muted">
                    <strong>Secuencia:</strong>{' '}
                    <div className="d-flex flex-wrap align-items-center">
                      {selectedPlan.centro_costo_peso.map((centroCostoPeso, index) => {
                        // Variables para almacenar nombre y peso
                        let nombre = '';
                        let peso = 0;
                        
                        // Obtener datos del centro de costo
                        if (selectedPlan.expand?.centro_costo_peso && 
                            selectedPlan.expand.centro_costo_peso[index]?.expand?.centro_costo?.nombre) {
                          nombre = selectedPlan.expand.centro_costo_peso[index].expand.centro_costo.nombre;
                          peso = selectedPlan.expand.centro_costo_peso[index].peso || 0;
                        }
                        else if (typeof centroCostoPeso === 'object' && centroCostoPeso?.expand?.centro_costo?.nombre) {
                          nombre = centroCostoPeso.expand.centro_costo.nombre;
                          peso = centroCostoPeso.peso || 0;
                        }
                        else {
                          // Como último recurso, usar el ID
                          let centroCostoId = '';
                          
                          if (typeof centroCostoPeso === 'string') {
                            centroCostoId = centroCostoPeso;
                            // Intentar obtener el peso de los datos expandidos
                            const expandedItem = selectedPlan.expand?.centro_costo_peso?.find((item: any) => item.id === centroCostoId);
                            if (expandedItem) {
                              peso = expandedItem.peso || 0;
                            }
                          } else if (typeof centroCostoPeso?.centro_costo === 'string') {
                            centroCostoId = centroCostoPeso.centro_costo;
                            peso = centroCostoPeso.peso || 0;
                          }
                          
                          if (centroCostoId && centroCostoId.length > 0) {
                            // Intentar obtener el nombre del mapa
                            if (getCentroCostoName && typeof getCentroCostoName === 'function') {
                              const nombreDelMapa = getCentroCostoName(centroCostoId);
                              if (nombreDelMapa && !nombreDelMapa.startsWith('Centro ')) {
                                nombre = nombreDelMapa;
                              } else {
                                nombre = `Centro ${centroCostoId.substring(0, 4)}`;
                              }
                            } else {
                              nombre = `Centro ${centroCostoId.substring(0, 4)}`;
                            }
                          } else {
                            nombre = 'Centro no especificado';
                          }
                        }
                        
                        return (
                          <React.Fragment key={index}>
                            {index > 0 && <span className="mx-1 text-muted">→</span>}
                            <div className="d-inline-block mb-1">
                              <span className="me-1">{nombre}</span>
                              <span className="badge bg-info rounded-pill">{peso}%</span>
                            </div>
                          </React.Fragment>
                        );
                      })}
                    </div>
                  </small>
                </div>
              )}
            </div>
          )}

          {/* Process Coverage Analysis */}
          {processValidation && (
            <div className="mb-3 p-3 border rounded">
              <h6 className="mb-2 text-info">
                <i className="fas fa-chart-pie me-2"></i>
                Análisis de Cobertura de Procesos
              </h6>
              
              {/* Validation Summary */}
              <div className={`alert ${processValidation.isValid ? 'alert-success' : 'alert-warning'} mb-3`}>
                <i className={`fas ${processValidation.isValid ? 'fa-check-circle' : 'fa-exclamation-triangle'} me-2`}></i>
                {processValidation.summary?.message}
              </div>

              {/* Coverage Details */}
              <div className="row">
                <div className="col-md-6">
                  <h6 className="text-muted">Procesos Requeridos:</h6>
                  <ul className="list-unstyled">
                    {processValidation.requiredProcesses?.map((process: string, index: number) => (
                      <li key={index} className="mb-1">
                        <i className="fas fa-circle me-2 text-primary" style={{ fontSize: '0.5rem' }}></i>
                        {process}
                      </li>
                    ))}
                    {(!processValidation.requiredProcesses || processValidation.requiredProcesses.length === 0) && (
                      <li className="text-muted">No se requieren procesos específicos</li>
                    )}
                  </ul>
                </div>
                
                <div className="col-md-6">
                  <h6 className="text-muted">Procesos Disponibles en el Plan:</h6>
                  <ul className="list-unstyled">
                    {processValidation.availableProcesses?.map((process: string, index: number) => (
                      <li key={index} className="mb-1">
                        <i className="fas fa-check-circle me-2 text-success" style={{ fontSize: '0.5rem' }}></i>
                        {process}
                      </li>
                    ))}
                    {(!processValidation.availableProcesses || processValidation.availableProcesses.length === 0) && (
                      <li className="text-muted">No hay procesos en el plan</li>
                    )}
                  </ul>
                </div>
              </div>

              {/* Missing Processes */}
              {processValidation.missingProcesses && processValidation.missingProcesses.length > 0 && (
                <div className="mt-3">
                  <h6 className="text-danger">Procesos Faltantes:</h6>
                  <ul className="list-unstyled">
                    {processValidation.missingProcesses.map((process: string, index: number) => (
                      <li key={index} className="mb-1">
                        <i className="fas fa-times-circle me-2 text-danger" style={{ fontSize: '0.5rem' }}></i>
                        {process}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Coverage Percentage */}
              <div className="mt-3">
                <div className="d-flex justify-content-between align-items-center">
                  <span className="text-muted">Cobertura de Procesos:</span>
                  <span className={`fw-bold ${processValidation.coveragePercentage === 100 ? 'text-success' : 'text-warning'}`}>
                    {processValidation.coveragePercentage}%
                  </span>
                </div>
                <div className="progress mt-1" style={{ height: '8px' }}>
                  <div 
                    className={`progress-bar ${processValidation.coveragePercentage === 100 ? 'bg-success' : 'bg-warning'}`}
                    style={{ width: `${processValidation.coveragePercentage}%` }}
                  ></div>
                </div>
              </div>
            </div>
          )}

          {/* Validation Loading */}
          {validationLoading && (
            <div className="text-center py-3">
              <Spinner size="sm" className="me-2">
                Validando...
              </Spinner>
              Validando cobertura de procesos...
            </div>
          )}

          {/* Error Display */}
          {asignacionError && (
            <Alert color="danger" className="mt-3">
              <i className="fas fa-exclamation-triangle me-2"></i>
              {asignacionError}
            </Alert>
          )}
          
          {/* Planes Error Display - Only show if no planes loaded and there's an error */}
          {planesError && planes.length === 0 && (
            <Alert color="warning" className="mt-3">
              <i className="fas fa-exclamation-triangle me-2"></i>
              {planesError}
            </Alert>
          )}

          {/* Loading State */}
          {planesLoading && (
            <div className="text-center py-3">
              <Spinner size="sm" className="me-2">
                Cargando...
              </Spinner>
              Cargando planes disponibles...
            </div>
          )}
        </ModalBody>

        <ModalFooter>
          <Button
            type="button"
            color="secondary"
            onClick={handleClose}
            disabled={asignacionLoading}
          >
            <i className="fas fa-times me-2"></i>
            Cancelar
          </Button>
          
          <Button
            type="submit"
            color="primary"
            disabled={!canSubmit}
            className="d-flex align-items-center"
          >
            {asignacionLoading ? (
              <>
                <Spinner size="sm" className="me-2">
                  Asignando...
                </Spinner>
                Asignando Plan...
              </>
            ) : (
              <>
                <i className="fas fa-check me-2"></i>
                Asignar Plan
              </>
            )}
          </Button>
        </ModalFooter>
      </Form>
    </Modal>
  );
};

export default AsignarPlanModal;
